import React from 'react';
import { MdKeyboardArrowDown, MdKeyboardArrowRight } from 'react-icons/md';
import { TreeItem } from './types';

export interface TreeNodeProps<T> {
  item: TreeItem<T>;
  level: number;
  isOpen: boolean;
  toggle: (id: string) => void;
  renderLabel: (data: T, item?: TreeItem<T>) => React.ReactNode;
  renderLeaf?: (data: T) => React.ReactNode;
  openFolders: Set<string>;
  onItemClick?: (item: TreeItem<T>) => void;
  selectedId?: string;
  className?: string;
}

export const TreeNode = <T,>({
  item,
  level,
  isOpen,
  toggle,
  renderLabel,
  renderLeaf,
  openFolders,
  onItemClick,
  selectedId,
  className = '',
}: TreeNodeProps<T>) => {
  const hasChildren = (item.children?.length || 0) > 0;

  const isSelected = selectedId === item.id;
  const paddingLeft = 0.5 + level * 1.5;

  return (
    <li className={`list-none ${className}`} style={{ listStyle: 'none' }}>
      <div
        className={`flex items-center gap-1 cursor-pointer hover:bg-gray-50 py-1 px-2 rounded-md transition-colors duration-150 ${
          isSelected ? 'bg-blue-50 text-blue-700 font-medium' : ''
        }`}
        style={{ paddingLeft: `${paddingLeft}rem` }}
      >
        {item.canOpen ? (
          <button
            onClick={() => {
              toggle(item.id);
              onItemClick?.(item);
            }}
            className="flex items-center justify-center w-4 h-4 text-gray-500 hover:text-gray-700 transition-colors duration-150 bg-transparent border-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-300 rounded"
            style={{ background: 'none', border: 'none', padding: '0' }}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
          >
            {isOpen ? <MdKeyboardArrowDown size={14} /> : <MdKeyboardArrowRight size={14} />}
          </button>
        ) : (
          <span className="w-4 h-4" />
        )}
        <div
          className="w-full"
          onClick={() => {
            onItemClick?.(item);
          }}
        >
          {renderLabel(item.data, item)}
        </div>
      </div>

      {isOpen &&
        hasChildren &&
        item.children?.map((child) => (
          <TreeNode
            key={child.id}
            item={child}
            level={level + 1}
            isOpen={openFolders.has(child.id)}
            toggle={toggle}
            renderLabel={renderLabel}
            renderLeaf={renderLeaf}
            openFolders={openFolders}
            onItemClick={onItemClick}
            selectedId={selectedId}
          />
        ))}

      {isOpen && item.hasLeaf && renderLeaf?.(item.data)}
    </li>
  );
};
